import { useMemo } from 'react';
import { useSignupDirectCtx } from '../context';
import { getCommunicationConcernOptionsFromKeys } from '../misc/communicationConcerns';
import { filtersAllowForEvaluation } from './evaluationCriteria';
import { filtersAllowSplitEvaluation } from './splitEvaluationCriteria';
import { leadCanPickSchedule } from './schedulingCriteria';

export enum SignupPath {
  SPLIT_EVALUATIONS = 'split_evaluations',
  SINGLE_EVALUATION = 'single_evaluation',
  CONSULTATION = 'consultation',
}

export const useSignupRoutingRules = () => {
  const { filters, isResumedLead, resumedLeadState } = useSignupDirectCtx();

  if (!filters) throw new Error('Expected filters to be set');

  const isEvalAllowed = useMemo(
    () =>
      filtersAllowForEvaluation({
        clinicalConcerns: getCommunicationConcernOptionsFromKeys(filters.clinicalConcernKeys, filters.therapyTarget),
        language: filters.language,
        paymentMethod: filters.paymentMethod,
        target: filters.therapyTarget,
        isResumedLead,
      }),
    [filters],
  );
  const isSplitEvaluationAllowed = useMemo(
    () =>
      filtersAllowSplitEvaluation({
        paymentMethod: filters.paymentMethod,
        clinicalConcerns: getCommunicationConcernOptionsFromKeys(filters.clinicalConcernKeys, filters.therapyTarget),
      }),
    [filters, isResumedLead],
  );

  const isScheduleAllowed = useMemo(
    () => leadCanPickSchedule({ resumedLeadState, isEvalAllowed: isEvalAllowed.allowed }),
    [filters, isResumedLead, isEvalAllowed],
  );

  const signupPath: SignupPath = useMemo(() => {
    if (isSplitEvaluationAllowed.allowed) {
      return SignupPath.SPLIT_EVALUATIONS;
    }

    if (isEvalAllowed.allowed) {
      return SignupPath.SINGLE_EVALUATION;
    }
    return SignupPath.CONSULTATION;
  }, [isEvalAllowed, isSplitEvaluationAllowed]);

  return {
    isEvalAllowed,
    isSplitEvaluationAllowed,
    isScheduleAllowed,
    signupPath,
  };
};
